import { ANNOTATION_VALUE } from "@/constants/annotation-values"

import type { MetricDirectoryEntryResource as MetricDirectoryEntry } from "@/types_api/backend/transport/list_metric_directory_response"
import type {
  MetricThresholdResource,
  ThresholdComparisonOperator,
} from "@/types/api/MetricThreshold"
import {
  FLOAT_COMPARISON_OPERATOR,
  RANGE_COMPARISON_OPERATOR,
  SET_COMPARISON_OPERATOR,
  type FloatComparisonOperator,
  type SetComparisonOperator,
  type TargetCondition,
} from "@/types/metrics/contracts"
import type { MetricMetadata } from "@/types/metrics/metadata"

import { isBinaryMetric } from "./isBinaryMetric"
import { isCategoricalMetric } from "./isCategoricalMetric"
import { isNumberMetric, isNumberMetricByManager } from "./isNumberMetric"

export const METRIC_TYPE = {
  NUMERICAL: "numerical",
  BINARY: "binary",
  CATEGORICAL: "categorical",
  SET: "set",
  UNTYPED: "untyped",
} as const

type MetricType = (typeof METRIC_TYPE)[keyof typeof METRIC_TYPE]

export interface MetricTypeSource {
  metric_name: string
  metric_manager: string
  output_type?: string
}

export interface MetricThresholdResolutionSource {
  metric_metadata?: Pick<MetricMetadata, "target_condition"> | null
  organization_threshold?: MetricThresholdResource | null
}

export interface ResolvedThreshold {
  comparison_operator: ThresholdComparisonOperator
  target_float_upper?: number
  target_float_lower?: number
  target_values?: string[]
  source: "organization_threshold" | "target_condition"
}

// Prefer entry.semantic_type when the directory provides one (matches backend
// catalog); else fall back to the hardcoded manager + name + output_type logic.
export function getMetricType(
  metric: MetricTypeSource,
  entry?: MetricDirectoryEntry,
): MetricType {
  // Explicit null/undefined cases satisfy the no-exhaustive-switch lint rule;
  // any unknown semantic_type falls through default to hardcoded logic.
  switch (entry?.semantic_type) {
    case "binary":
      return METRIC_TYPE.BINARY
    case "categorical":
    case "sentiment":
      return METRIC_TYPE.CATEGORICAL
    case "numerical":
      return METRIC_TYPE.NUMERICAL
    case undefined:
    case null:
    default:
      break
  }
  if (isBinaryMetric(metric)) {
    return METRIC_TYPE.BINARY
  }
  if (isCategoricalMetric(metric)) {
    return METRIC_TYPE.CATEGORICAL
  }
  if (
    isNumberMetricByManager(metric) ||
    isNumberMetric(metric.metric_name) ||
    metric.output_type === "float"
  ) {
    return METRIC_TYPE.NUMERICAL
  }
  if (metric.output_type === "set") {
    return METRIC_TYPE.SET
  }
  return METRIC_TYPE.UNTYPED
}

/**
 * Valid threshold operators for each metric type.
 */
export function getValidOperatorsForType(
  type: MetricType,
): ThresholdComparisonOperator[] {
  switch (type) {
    case METRIC_TYPE.NUMERICAL:
      return [
        FLOAT_COMPARISON_OPERATOR.EQ,
        FLOAT_COMPARISON_OPERATOR.NEQ,
        FLOAT_COMPARISON_OPERATOR.GT,
        FLOAT_COMPARISON_OPERATOR.GTE,
        FLOAT_COMPARISON_OPERATOR.LT,
        FLOAT_COMPARISON_OPERATOR.LTE,
        RANGE_COMPARISON_OPERATOR.BT,
        RANGE_COMPARISON_OPERATOR.NBT,
      ]
    case METRIC_TYPE.BINARY:
      return [
        FLOAT_COMPARISON_OPERATOR.GT,
        FLOAT_COMPARISON_OPERATOR.GTE,
        FLOAT_COMPARISON_OPERATOR.LT,
        FLOAT_COMPARISON_OPERATOR.LTE,
      ]
    case METRIC_TYPE.CATEGORICAL:
    case METRIC_TYPE.SET:
      return [SET_COMPARISON_OPERATOR.IN, SET_COMPARISON_OPERATOR.NIN]
    case METRIC_TYPE.UNTYPED:
      return []
  }
}

export function isRangeOperator(
  op: ThresholdComparisonOperator,
): op is "bt" | "nbt" {
  return (
    op === RANGE_COMPARISON_OPERATOR.BT || op === RANGE_COMPARISON_OPERATOR.NBT
  )
}

/**
 * Resolve the effective threshold for a metric.
 *
 * Precedence: organization_threshold > metric_metadata.target_condition
 *
 * Returns null if no threshold is configured.
 */
export function resolveThreshold(
  metric: MetricThresholdResolutionSource,
): ResolvedThreshold | null {
  // 1. Organization threshold takes priority
  const orgThreshold = metric.organization_threshold
  if (orgThreshold?.comparison_operator != null) {
    const resolved: ResolvedThreshold = {
      comparison_operator: orgThreshold.comparison_operator,
      source: "organization_threshold",
    }
    if (orgThreshold.target_float_upper != null) {
      resolved.target_float_upper = orgThreshold.target_float_upper
    }
    if (orgThreshold.target_float_lower != null) {
      resolved.target_float_lower = orgThreshold.target_float_lower
    }
    if (orgThreshold.target_values != null) {
      resolved.target_values = orgThreshold.target_values
    }
    return resolved
  }

  // 2. Fall back to legacy target_condition in metric_metadata
  const tc = metric.metric_metadata?.target_condition
  if (!tc) {
    return null
  }

  if ("target_float" in tc) {
    return {
      comparison_operator:
        tc.comparison_operator as ThresholdComparisonOperator,
      target_float_upper: tc.target_float,
      source: "target_condition",
    }
  }

  if ("target_values" in tc) {
    return {
      comparison_operator:
        tc.comparison_operator as ThresholdComparisonOperator,
      target_values: tc.target_values,
      source: "target_condition",
    }
  }

  return null
}

/**
 * For binary metrics on a success-rate chart: convert the stored float threshold
 * to the corresponding success rate value (0–1).
 *
 * lte/lt operators indicate inverted metrics (e.g. "failure rate ≤ 0.10")
 * where success rate = 1 − floatTarget.
 * gte/gt operators indicate direct metrics where success rate = floatTarget.
 */
export function getBinarySuccessThreshold(
  threshold: ResolvedThreshold,
): number {
  const op = threshold.comparison_operator
  const floatTarget = threshold.target_float_upper ?? 0
  return op === FLOAT_COMPARISON_OPERATOR.LTE ||
    op === FLOAT_COMPARISON_OPERATOR.LT
    ? 1 - floatTarget
    : floatTarget
}

/**
 * Convert a ResolvedThreshold to a TargetCondition for backend success-rate queries.
 * Returns null for range operators (bt/nbt) since the backend doesn't support them.
 */
export function resolvedThresholdToTargetCondition(
  threshold: ResolvedThreshold,
): TargetCondition | null {
  const op = threshold.comparison_operator
  if (
    op === RANGE_COMPARISON_OPERATOR.BT ||
    op === RANGE_COMPARISON_OPERATOR.NBT
  ) {
    return null
  }
  if (threshold.target_float_upper != null) {
    return {
      target_float: threshold.target_float_upper,
      comparison_operator: op as FloatComparisonOperator,
    }
  }
  if (threshold.target_values != null) {
    return {
      target_values: threshold.target_values,
      comparison_operator: op as SetComparisonOperator,
    }
  }
  return null
}

/**
 * Determine the "success" string value for a binary metric.
 *
 * Resolution order:
 * 1. If `invertColors` is true, returns "NO"
 * 2. If a resolved threshold has `target_values`, returns the first entry
 * 3. Falls back to "YES"
 */
export function resolveSuccessValue(
  metric: MetricThresholdResolutionSource,
  invertColors = false,
): string {
  if (invertColors) {
    return ANNOTATION_VALUE.NO
  }
  const threshold = resolveThreshold(metric)
  if (threshold?.target_values?.length) {
    return threshold.target_values[0]
  }
  return ANNOTATION_VALUE.YES
}

/**
 * Convert a ResolvedThreshold back to a MetricThresholdResource shape
 * suitable for passing to backend aggregation queries.
 */
export function resolvedThresholdToResource(
  resolved: ResolvedThreshold,
  metricId: string,
): MetricThresholdResource {
  return {
    name: metricId,
    comparison_operator: resolved.comparison_operator,
    target_float_upper: resolved.target_float_upper ?? null,
    target_float_lower: resolved.target_float_lower ?? null,
    target_values: resolved.target_values ?? null,
    source: "manual",
    create_time: "",
    update_time: "",
  }
}
